import React, { useState, useEffect, useMemo } from 'react';
import { useAppShell } from '../contexts/AppShellContext';
import { getMaterialReceipts } from '../services/materialReceiptService';
import type { MaterialReceiptRow } from '../types/api';
import { SearchInput } from '../components/SearchInput';
import { SortableTh } from '../components/SortableTh';
import { useSort, sortRows } from '../utils/useSort';
import { pillStyle } from '../utils/pillStyle';
import { CATEGORY_LABEL, CATEGORY_FILTERS, CATEGORY_PILL } from '../utils/jobCategory';

// Inventory is derived from the `location` field on material receipts. Main holds
// stock on the large warehouse shelves (any location other than "overs"); Overs is
// the small-box area for excess material split off a parent receipt.
const TABS = [
  { id: 'main',  label: 'Main' },
  { id: 'overs', label: 'Overs' },
] as const;
type TabId = typeof TABS[number]['id'];

// Location leads — inventory is organised by where stock physically sits. With 100+
// shelf locations a flat sortable/filterable column scales far better than per-location
// section groups (which would be mostly headers), so location is a column + a filter.
const COLUMNS = ['Location', 'Received', 'Category', 'Client Name', 'Document Type', 'Quantity'];

const ALL_LOCATIONS = 'All locations';

// Category code → tag class (mirrors the All Receipts category tags).
const CATEGORY_CLS: Record<string, string> = { P: 'tp tp-px', CA: 'tp tp-ca', R: 'tp tp-rg', I: 'tp tp-px' };

// One inventory row per document type: a receipt's received_docs map is exploded
// so a client with several material types shows one row per type.
interface InventoryRow {
  key: string;
  receipt: MaterialReceiptRow;
  docType: string;
  qty: number | null;
}

function explode(receipts: MaterialReceiptRow[]): InventoryRow[] {
  const out: InventoryRow[] = [];
  for (const receipt of receipts) {
    const docs = Object.entries(receipt.received_docs || {});
    if (docs.length === 0) {
      // No itemised docs — still surface the item so it isn't hidden from inventory.
      out.push({ key: `${receipt.id}::—`, receipt, docType: '—', qty: null });
      continue;
    }
    docs.forEach(([docType, qty], i) => {
      out.push({ key: `${receipt.id}::${docType}::${i}`, receipt, docType, qty: qty == null ? null : Number(qty) });
    });
  }
  return out;
}

// Location code, normalised: blank/empty → "—" (not yet shelved) so it sorts/filters consistently.
function locOf(r: MaterialReceiptRow): string {
  return r.location && r.location.trim() ? r.location : '—';
}

function cellValue(r: InventoryRow, column: string): string | number {
  switch (column) {
    case 'Location':      return locOf(r.receipt);
    case 'Received':      return r.receipt.intake_date ?? '';
    case 'Category':      return r.receipt.job_category ? (CATEGORY_LABEL[r.receipt.job_category] ?? r.receipt.job_category) : '';
    case 'Client Name':   return r.receipt.client_names.join(', ');
    case 'Document Type': return r.docType;
    case 'Quantity':      return r.qty ?? -1;
    default:              return '';
  }
}

function CategoryTag({ category }: { category: string | null }) {
  if (!category) return <span className="text-xs text-muted">—</span>;
  return <span className={CATEGORY_CLS[category] ?? 'tp tp-px'}>{CATEGORY_LABEL[category] ?? category}</span>;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${parseInt(m)}/${parseInt(d)}/${y}`;
}

function isOvers(r: MaterialReceiptRow): boolean {
  // Legacy split-off overs receipts, plus confirmed Overs follow-ups (excess stock
  // from a completed job that an operator has confirmed + shelved).
  return (r.location === 'overs' && r.material_status === 'Overs') || r.overs_status === 'Completed';
}

export function InventoryPage() {
  const { setTopbar } = useAppShell();

  const [receipts, setReceipts] = useState<MaterialReceiptRow[]>([]);
  const [tab, setTab]           = useState<TabId>('main');
  const [category, setCategory] = useState('All');
  const [loc, setLoc]           = useState(ALL_LOCATIONS);
  const [search, setSearch]     = useState('');
  const { sort, toggle }        = useSort({ column: 'Location', direction: 'asc' });
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  useEffect(() => { setTopbar('Inventory', 'Live warehouse stock by location'); }, [setTopbar]);

  useEffect(() => {
    let alive = true;
    const load = () => getMaterialReceipts()
      .then(r => { if (alive) setReceipts(r); })
      .catch(e => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    load();
    const iv = setInterval(load, 3000);  // live: reflect new receipts + status changes
    return () => { alive = false; clearInterval(iv); };
  }, []);

  // Tab partition by location: Overs = location "overs" + status "Overs"; Main = everything else.
  const tabRows = useMemo(() => {
    const main: InventoryRow[]  = [];
    const overs: InventoryRow[] = [];
    for (const row of explode(receipts)) {
      (isOvers(row.receipt) ? overs : main).push(row);
    }
    return { main, overs };
  }, [receipts]);

  const counts = { main: tabRows.main.length, overs: tabRows.overs.length };

  // Distinct shelf locations in the active tab — feeds the location filter dropdown.
  const locations = useMemo(() => {
    const set = new Set<string>();
    tabRows[tab].forEach(r => set.add(locOf(r.receipt)));
    return Array.from(set).sort();
  }, [tabRows, tab]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const data = tabRows[tab].filter(r => {
      if (category !== 'All' && r.receipt.job_category !== category) return false;
      if (loc !== ALL_LOCATIONS && locOf(r.receipt) !== loc) return false;
      if (q && !`${locOf(r.receipt)} ${r.receipt.client_names.join(' ')} ${r.docType}`.toLowerCase().includes(q)) return false;
      return true;
    });
    return sortRows(data, sort, cellValue);
  }, [tabRows, tab, category, loc, search, sort]);

  if (error) return <div className="px-6 py-5 text-sm text-red-600">{error}</div>;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Tabs — Main / Overs (mirrors Receiving Log) */}
      <div className="flex gap-0 px-6 border-b border-trim flex-shrink-0">
        {TABS.map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setCategory('All'); setLoc(ALL_LOCATIONS); }}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap"
            style={{
              borderColor: tab === t.id ? '#2D7575' : 'transparent',
              color: tab === t.id ? '#256060' : '#73747A',
            }}>
            {t.label}
            <span className="px-1.5 py-0.5 rounded-full text-xs font-bold"
              style={{
                background: tab === t.id ? '#2D7575' : '#F5F7FA',
                color: tab === t.id ? 'white' : '#73747A',
              }}>
              {counts[t.id]}
            </span>
          </button>
        ))}
      </div>

      {/* Filter row — count chip, category pills, search */}
      <div className="flex items-center gap-3 px-6 pt-4 pb-4 border-b border-trim flex-shrink-0 flex-wrap gap-y-2">
        <div className="px-2.5 py-0.5 rounded-full text-xs font-semibold" style={{ background: '#F5F7FA', color: '#595A60' }}>{filtered.length} items</div>
        <div className="flex gap-1.5 flex-wrap">
          {CATEGORY_FILTERS.map(c => (
            <button key={c.id} onClick={() => setCategory(c.id)} className="px-3 py-1 rounded-full text-xs font-medium transition-colors"
              style={pillStyle(category === c.id, CATEGORY_PILL[c.id])}>{c.label}</button>
          ))}
        </div>
        {/* Location filter — jump straight to a shelf. Scales to 100+ locations as a dropdown. */}
        <select
          value={loc}
          onChange={e => setLoc(e.target.value)}
          className="px-2.5 py-1 rounded-lg border border-trim text-xs text-ink bg-white outline-none focus:border-teal-400 cursor-pointer"
          title="Filter by location"
        >
          <option value={ALL_LOCATIONS}>{ALL_LOCATIONS} ({locations.length})</option>
          {locations.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <div className="ml-auto">
          <SearchInput value={search} onChange={setSearch} placeholder="Search location, clients, document types…" width={260} />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 pb-6">
        {loading && <div className="text-sm text-muted py-4">Loading…</div>}
        <table className="text-xs" style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
          <thead>
            <tr>
              {COLUMNS.map(h => (
                <SortableTh key={h} label={h} column={h} sort={sort} onSort={toggle} />
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.key} className="hover:bg-wash transition-colors">
                <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap align-top">
                  <span className="text-xs font-semibold text-ink">{locOf(r.receipt)}</span>
                </td>
                <td className="px-2 py-1.5 border-b border-wash text-xs text-muted whitespace-nowrap align-top">{fmtDate(r.receipt.intake_date)}</td>
                <td className="px-2 py-1.5 border-b border-wash whitespace-nowrap align-top"><CategoryTag category={r.receipt.job_category} /></td>
                <td className="px-2 py-1.5 border-b border-wash align-top">
                  <div className="max-w-[280px] text-xs text-copy break-words" title={r.receipt.client_names.join(', ')}>{r.receipt.client_names.join(', ')}</div>
                </td>
                <td className="px-2 py-1.5 border-b border-wash text-xs text-ink align-top">{r.docType}</td>
                <td className="px-2 py-1.5 border-b border-wash text-xs text-ink whitespace-nowrap align-top">{r.qty ?? '—'}</td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={COLUMNS.length} className="text-center py-12 text-muted text-sm">No inventory matches the current filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
